import { useEffect } from "react";
import { isAppHeightFrozen, isAppHeightPinned } from "./useStaticAppHeight";

/** How long to keep re-asserting document scroll after a focus change or a
    viewport resize. WebKit's keyboard animation runs about 250ms; this covers
    it with margin and then stops, so nothing runs while the app sits idle. */
const KEYBOARD_SETTLE_MS = 600;

/** How long a shrinking height must hold steady before it is committed.

    iOS does not emit one resize when the keyboard opens; it emits several,
    and the intermediate ones report heights matching no final keyboard
    position — the first arrives without the Input Accessory Bar and is about
    50px shorter than the one after it. Committing each in turn makes the
    composer jitter through the animation and can leave it sized for a value
    that no longer applies.

    Growth is deliberately exempt: a keyboard going away is reported once and
    is honoured on the frame it arrives, so closing stays instant. */
const STABILITY_MS = 80;

/** Writes the visible viewport's height to `--app-height` on
    `document.documentElement`, which `#root` reads in `styles.css`, and holds
    document scroll at zero.

    What this hook can and cannot do was settled by tracing every frame of a
    keyboard open on a real installed iPhone app:

      vv.offsetTop   0 … 376
      scrollTop      0
      #root top      0
      header top     0

    Everything the page controls is already still — #root, the header and
    document scroll never move. What moves is the visual viewport: WebKit
    *pans* the window over a static page. A pan cannot be countered from CSS,
    because it moves fixed elements too, and WebKit reports it only once it
    has finished, so it cannot be tracked either. Two earlier rounds tried
    both and failed.

    The pan is prevented at its source instead, in Composer: iOS runs a
    synchronous "will this element be visible once the keyboard is up?" check
    inside its own focus handling and pans when it decides no, so the composer
    suppresses that handling and calls `focus({ preventScroll: true })`
    itself. See the note there — that is the fix; this hook is what makes the
    result hold still afterwards.

    `--app-height` comes from `visualViewport.height` rather than `dvh`, which
    resolves against a layout viewport that settles 20px taller than what is
    really visible — enough to hide the composer under the AutoFill accessory
    bar.

    Tracking the keyboard is what chat needs and not what every screen needs:
    a screen whose layout must not react to the keyboard at all can freeze the
    shrink for as long as it is mounted with useStaticAppHeight, and one
    anchored to #root's bottom edge can freeze growth too. Rotation and the
    scroll lock are unaffected by either — in particular the scroll lock still
    runs, because holding still is exactly what a static screen needs too. */
export function useAppViewport(): void {
  useEffect(() => {
    const root = document.documentElement;
    const viewport = window.visualViewport;
    let frame: number | null = null;
    let pumpUntil = 0;
    let applied = 0;
    let pending = 0;
    /* Rotation genuinely changes how tall the app is, so a frozen screen must
       not be left sized for the previous orientation. The window covers
       iOS reporting the new size a few frames after the event. */
    let bypassUntil = 0;
    let stabilityTimer: ReturnType<typeof setTimeout> | null = null;

    function measure(): number {
      return viewport ? viewport.height * viewport.scale : window.innerHeight;
    }

    /* Synchronous, never deferred: a scroll left standing for even one frame
       is a frame with the app dragged out of the visible area. Nothing in
       this app is ever taller than the viewport — every screen scrolls inside
       its own panel — so document scroll should always read zero and there is
       no content below y=0 for this to reveal or hide. */
    function lockScroll() {
      const scroller = document.scrollingElement;
      if (scroller && scroller.scrollTop !== 0) {
        scroller.scrollTop = 0;
      }
      if (window.scrollX !== 0 || window.scrollY !== 0) {
        window.scrollTo(0, 0);
      }
    }

    function clearStability() {
      if (stabilityTimer !== null) {
        clearTimeout(stabilityTimer);
        stabilityTimer = null;
      }
    }

    function commit(height: number) {
      applied = height;
      pending = height;
      root.style.setProperty("--app-height", `${height}px`);
    }

    function write() {
      const measured = measure();

      if (measured >= applied) {
        // A pinned screen holds its height in both directions: a mobile
        // browser retracting its own toolbar as the keyboard opens arrives
        // here as growth, and letting it through drags anything anchored to
        // #root's bottom edge down under the keyboard (see
        // useStaticAppHeight's freezeGrowth).
        if (isAppHeightPinned() && performance.now() >= bypassUntil) {
          clearStability();
          pending = applied;
          return;
        }
        clearStability();
        commit(measured);
        return;
      }

      // A screen that wants a static layout takes the shrink out of play (see
      // useStaticAppHeight). Only the shrink: growth above still commits, so
      // the keyboard closing recovers on its own.
      if (isAppHeightFrozen() && performance.now() >= bypassUntil) {
        clearStability();
        pending = applied;
        return;
      }

      // Shrinking: restart the window, so only a height that outlives it
      // commits. Re-measuring the same value must not restart it, or the
      // per-frame pump below would hold the timer off indefinitely.
      if (measured === pending) {
        return;
      }
      pending = measured;
      clearStability();
      stabilityTimer = setTimeout(() => {
        stabilityTimer = null;
        commit(pending);
      }, STABILITY_MS);
    }

    function tick() {
      frame = null;
      lockScroll();
      write();
      if (performance.now() < pumpUntil) {
        frame = requestAnimationFrame(tick);
      }
    }

    function schedule() {
      // Immediately, not on the next frame: see lockScroll.
      lockScroll();
      write();
      pumpUntil = performance.now() + KEYBOARD_SETTLE_MS;
      frame ??= requestAnimationFrame(tick);
    }

    function rotate() {
      bypassUntil = performance.now() + KEYBOARD_SETTLE_MS;
      schedule();
    }

    lockScroll();
    commit(measure());

    document.addEventListener("focusin", schedule);
    document.addEventListener("focusout", schedule);
    window.addEventListener("scroll", schedule, { passive: true });
    viewport?.addEventListener("resize", schedule);
    viewport?.addEventListener("scroll", schedule);
    window.addEventListener("resize", schedule);
    window.addEventListener("orientationchange", rotate);

    return () => {
      if (frame !== null) {
        cancelAnimationFrame(frame);
      }
      clearStability();
      document.removeEventListener("focusin", schedule);
      document.removeEventListener("focusout", schedule);
      window.removeEventListener("scroll", schedule);
      viewport?.removeEventListener("resize", schedule);
      viewport?.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      window.removeEventListener("orientationchange", rotate);
      root.style.removeProperty("--app-height");
    };
  }, []);
}
